/**
 * routes.js
 * OnTimeLog 시스템 라우팅 구조
 * Mobile First 전략 - 모바일/PC 분리 라우팅
 * 
 * ⚠️ 중요: 라우팅 규칙 (변경 금지)
 * ============================================
 * 
 * PC 라우트 (/p/*):
 *   - /p/dashboard     → Dashboard 컴포넌트 (PC 버전)
 *   - /p/job-time-input → JobTimeInput 컴포넌트 (PC 버전)
 *   - /p/todo-list     → TodoList 컴포넌트
 *   - /p/admin         → Admin 컴포넌트 (PC 버전)
 * 
 * 모바일 라우트 (/m/*):
 *   - /m/dashboard     → MobileDashboard 컴포넌트 (모바일 버전)
 *   - /m/log           → MobileJobTimeInput 컴포넌트 (모바일 버전)
 *   - /m/todo          → TodoList 컴포넌트
 *   - /m/admin         → /p/admin로 리다이렉트 (모바일에서는 관리자 페이지 접근 불가)
 * 
 * ⚠️ 절대 변경 금지:
 *   - /p/dashboard는 반드시 Dashboard 컴포넌트를 사용해야 함
 *   - /m/dashboard는 반드시 MobileDashboard 컴포넌트를 사용해야 함
 *   - /p/job-time-input는 반드시 JobTimeInput 컴포넌트를 사용해야 함
 *   - /m/log는 반드시 MobileJobTimeInput 컴포넌트를 사용해야 함
 * 
 * 이 규칙은 App.js의 Layout 선택 로직과 연결되어 있습니다:
 *   - /p/* 경로 → MainLayout (PC 레이아웃)
 *   - /m/* 경로 → MobileLayout (모바일 레이아웃)
 * 
 * 변경 이력:
 *   - V15: 경로 기반 Layout 선택으로 변경 (App.js에서 isMobilePath 사용)
 *   - 이전 버전에서 라우팅이 반대로 설정되어 있었던 문제를 수정함
 * 
 * 자세한 내용은 ROUTING_RULES.md 문서를 참조하세요.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { detectMobileDevice } from './utils/deviceDetection';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import JobTimeInput from './pages/JobTimeInput';
import TodoList from './pages/TodoList';
import Admin from './pages/Admin';
import WorkDetailsReport from './pages/WorkDetailsReport';
import ExperienceSearch from './pages/ExperienceSearch';
import P2PChat from './pages/P2PChat';

// Mobile Pages
import MobileJobTimeInput from './pages/MobileJobTimeInput';
import MobileDashboard from './pages/MobileDashboard';

// 모바일 뷰 및 사용자 역할에 따른 홈 라우팅
// 로그인 후 최초 화면은 항상 대시보드
const HomeRedirect = () => {
  const isLoggedIn = typeof localStorage !== 'undefined' ? localStorage.getItem('isLoggedIn') === 'true' : false;
  
  if (!isLoggedIn) {
    // 로그인하지 않은 경우 로그인 페이지로 리다이렉트
    return <Navigate to="/login" replace />;
  }
  
  // 모바일 디바이스 감지
  const isMobile = detectMobileDevice();
  
  // 로그인된 경우 대시보드로 이동 (모바일 디바이스 감지 우선)
  if (isMobile) {
    return <Navigate to="/m/dashboard" replace />;
  } else {
    return <Navigate to="/p/dashboard" replace />;
  }
};

// 로그인 페이지 (인증 불필요)
const publicRoutes = [
  {
    path: '/login',
    element: <Login />
  },
];

// PC 라우트 (인증 필요)
// ⚠️ 중요: /p/* 경로는 반드시 PC 버전 컴포넌트를 사용해야 함
// MainLayout과 함께 사용되며, Dashboard, JobTimeInput 등 PC 전용 컴포넌트를 렌더링함
const pcRoutes = [
  {
    path: '/p/dashboard',
    // ⚠️ 변경 금지: Dashboard 컴포넌트 (PC 버전) - MobileDashboard 아님
    element: <ProtectedRoute><Dashboard /></ProtectedRoute>
  },
  {
    path: '/p/job-time-input',
    // ⚠️ 변경 금지: JobTimeInput 컴포넌트 (PC 버전) - MobileJobTimeInput 아님
    element: <ProtectedRoute><JobTimeInput /></ProtectedRoute>
  },
  {
    path: '/p/todo-list',
    element: <ProtectedRoute><TodoList /></ProtectedRoute>
  },
  {
    path: '/p/work-details-report',
    element: <ProtectedRoute><WorkDetailsReport /></ProtectedRoute>
  },
  {
    path: '/p/experience-search',
    element: <ProtectedRoute><ExperienceSearch /></ProtectedRoute>
  },
  {
    path: '/p/p2p-chat',
    element: <ProtectedRoute><P2PChat /></ProtectedRoute>
  },
  {
    path: '/p/admin',
    // ⚠️ 변경 금지: Admin 컴포넌트 (PC 버전)
    element: <ProtectedRoute requiredRole={['admin', 'root']}><Admin /></ProtectedRoute>
  },
];

// 모바일 라우트 (인증 필요)
// ⚠️ 중요: /m/* 경로는 반드시 모바일 버전 컴포넌트를 사용해야 함
// MobileLayout과 함께 사용되며, MobileDashboard, MobileJobTimeInput 등 모바일 전용 컴포넌트를 렌더링함
const mobileRoutes = [
  {
    path: '/m/log',
    // ⚠️ 변경 금지: MobileJobTimeInput 컴포넌트 (모바일 버전) - JobTimeInput 아님
    element: <ProtectedRoute><MobileJobTimeInput /></ProtectedRoute>
  },
  {
    path: '/m/todo',
    element: <ProtectedRoute><TodoList /></ProtectedRoute>
  },
  {
    path: '/m/work-details-report',
    element: <ProtectedRoute><WorkDetailsReport /></ProtectedRoute>
  },
  {
    path: '/m/experience-search',
    element: <ProtectedRoute><ExperienceSearch /></ProtectedRoute>
  },
  {
    path: '/m/p2p-chat',
    element: <ProtectedRoute><P2PChat /></ProtectedRoute>
  },
  {
    path: '/m/dashboard',
    // ⚠️ 변경 금지: MobileDashboard 컴포넌트 (모바일 버전) - Dashboard 아님
    element: <ProtectedRoute><MobileDashboard /></ProtectedRoute>
  },
  // 모바일에서는 관리자 페이지 접근 불가 - PC 버전으로 리다이렉트 (권한 체크 포함)
  {
    path: '/m/admin',
    element: <ProtectedRoute requiredRole={['admin', 'root']}><Navigate to="/p/admin" replace /></ProtectedRoute>
  },
];

// 하위 호환성을 위한 기존 라우트 (PC로 리다이렉트)
const legacyRoutes = [
  {
    path: '/dashboard',
    element: <ProtectedRoute><Navigate to="/p/dashboard" replace /></ProtectedRoute>
  },
  {
    path: '/job-time-input',
    element: <ProtectedRoute><Navigate to="/p/job-time-input" replace /></ProtectedRoute>
  },
  {
    path: '/todo-list',
    element: <ProtectedRoute><Navigate to="/p/todo-list" replace /></ProtectedRoute>
  },
  {
    path: '/work-details-report',
    element: <ProtectedRoute><Navigate to="/p/work-details-report" replace /></ProtectedRoute>
  },
  {
    path: '/experience-search',
    element: <ProtectedRoute><Navigate to="/p/experience-search" replace /></ProtectedRoute>
  },
  {
    path: '/p2p-chat',
    element: <ProtectedRoute><Navigate to="/p/p2p-chat" replace /></ProtectedRoute>
  },
  {
    path: '/admin',
    element: <ProtectedRoute requiredRole={['admin', 'root']}><Navigate to="/p/admin" replace /></ProtectedRoute>
  },
];

const routes = [
  {
    path: '/',
    element: <HomeRedirect />
  },
  ...publicRoutes,
  ...pcRoutes,
  ...mobileRoutes,
  ...legacyRoutes,
];

export default routes;
